import workflowDashboardImage from "@/assets/automations-execution.webp";
import { Button } from "@/components/flexkit/Button";
import { Pill } from "@/components/podkit/status/Pill";
import { toast } from "@/components/podkit/toasts/use-toast";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text as Paragraph } from "@/components/podkit/typography/Text";

import { FeatureItem } from "@/components/education/FeatureItem";
import { EducationPageContainer } from "@/components/education/EducationPageContainer";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useGetAccount } from "@/queries/account-queries";
import { AlertTriangleIcon, GitBranchIcon, ShieldCheckIcon, PlusIcon } from "lucide-react";
import { type FC, useCallback } from "react";

const WORKFLOWS_EARLY_ACCESS_REQUESTED = "WORKFLOWS_EARLY_ACCESS_REQUESTED";

export const AutomationsEducationPage: FC = () => {
    const segmentTrack = useSegmentTrack();
    const { data: account } = useGetAccount();

    const { value: earlyAccessRequestedValue, setValueAsync: setEarlyAccessRequestedValue } = usePreferenceValue(
        WORKFLOWS_EARLY_ACCESS_REQUESTED,
    );
    const earlyAccessRequested = earlyAccessRequestedValue === "true";

    const handleRequestEarlyAccess = useCallback(async () => {
        try {
            segmentTrack("waitlist_joined", { feature: "Ona Automations", type: "automations", email: account?.email });
            await setEarlyAccessRequestedValue("true");
            toast({
                title: "Early access requested",
                description: "We'll be in touch about early access to automations.",
            });
        } catch (error) {
            toast({
                title: "Failed to request early access",
                description: error instanceof Error ? error.message : "An unknown error occurred.",
            });
        }
    }, [account?.email, segmentTrack, setEarlyAccessRequestedValue]);

    return (
        <EducationPageContainer>
            <div className="flex flex-col gap-2">
                <Pill variant="brand" size="lg" className="w-fit">
                    Available for Preview
                </Pill>
                <Heading1>Build automations that scale across every repo.</Heading1>
                <Paragraph className="text-content-secondary text-md">
                    Chain agents and commands into multi-step Automations that run across your entire codebase - scheduled or event driven.
                </Paragraph>
                
                <div className="mt-2">
                    <Button
                        size="md"
                        onClick={handleRequestEarlyAccess}
                        disabled={earlyAccessRequested}
                        data-tracking-id="request-early-access-workflows-education-page"
                    >
                        {earlyAccessRequested ? "Early Access Requested" : "Request early access"}
                    </Button>
                </div>
            </div>

            <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                {/* Image - shows first on mobile, second on desktop */}
                <img
                    src={workflowDashboardImage}
                    alt="Ona Automations dashboard"
                    className="order-1 w-full rounded-lg shadow-xl lg:order-2 lg:w-[65%]"
                />

                {/* Features - shows second on mobile, first on desktop */}
                <div className="order-2 flex flex-col gap-3 lg:order-1 lg:w-[35%]">
                    <Paragraph className="text-base font-medium text-content-primary">Use automations to:</Paragraph>
                    
                    <FeatureItem
                        icon={<ShieldCheckIcon size={16} />}
                        variant="success"
                        title="Enforce Compliance and Governance"
                        description="Automate PR reviews and compliance checks with audit-ready reports and documentation."
                    />

                    <FeatureItem
                        icon={<AlertTriangleIcon size={16} />}
                        variant="warning"
                        title="Flag and fix vulnerabilities"
                        description="Automate scans, patching, and open pull requests for review - reducing remediation time to hours."
                    />

                    <FeatureItem
                        icon={<GitBranchIcon size={16} />}
                        variant="brand-purple"
                        title="Standardize and Migrate at Scale"
                        description="Upgrade language versions, unify configurations, or migrate infrastructure through a single defined window."
                    />

                    <FeatureItem
                        icon={<PlusIcon size={16} />}
                        variant="brand"
                        title="Or design your own Automation"
                        description="Let Ona handle the routines, so you can stay focused on building."
                    />
                </div>
            </div>
        </EducationPageContainer>
    );
};
